import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  Check,
  CircleDollarSign,
  ExternalLink,
  HelpCircle,
  Loader2,
  MessageCircle,
  RotateCcw,
  ThumbsUp,
} from 'lucide-react'
import clsx from 'clsx'
import { fetchReplies, patchReply, syncReplies, type ReplyIntent, type ReplyItem } from '@/lib/data'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import { PageHeader } from '@/components/PageHeader'

type IntentFilter = ReplyIntent | 'all'

const INTENT_META: Record<ReplyIntent, { label: string; icon: React.ReactNode; chip: string }> = {
  lead: {
    label: 'Lead',
    icon: <CircleDollarSign size={12} />,
    chip: 'border-accent-green/40 bg-accent-green/10 text-accent-green',
  },
  question: {
    label: 'Question',
    icon: <HelpCircle size={12} />,
    chip: 'border-brand-200 bg-brand-50 text-brand-600',
  },
  praise: {
    label: 'Praise',
    icon: <ThumbsUp size={12} />,
    chip: 'border-accent-pink/40 bg-accent-pink/10 text-accent-pink',
  },
  other: {
    label: 'Reply',
    icon: <MessageCircle size={12} />,
    chip: 'border-line bg-surface text-ink-500',
  },
}

const FILTERS: { key: IntentFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'lead', label: 'Leads' },
  { key: 'question', label: 'Questions' },
  { key: 'praise', label: 'Praise' },
  { key: 'other', label: 'Everything else' },
]

function platformGlyph(platform: string) {
  const p = PLATFORMS[platform as PlatformId]
  if (!p) return null
  return (
    <span
      className="grid h-4 w-4 shrink-0 place-items-center"
      style={{ color: p.color === '#ffffff' ? '#111' : p.color }}
    >
      <span className="inline-flex h-4 w-4">{p.icon}</span>
    </span>
  )
}

export function RepliesPage() {
  const [replies, setReplies] = useState<ReplyItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<IntentFilter>('all')
  const [openOnly, setOpenOnly] = useState<boolean>(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await syncReplies()
      setReplies(await fetchReplies())
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load replies')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const counts = useMemo(() => {
    const open = replies.filter((r) => !r.doneAt)
    const c: Record<IntentFilter, number> = {
      all: open.length,
      lead: 0,
      question: 0,
      praise: 0,
      other: 0,
    }
    for (const r of open) c[r.intent]++
    return c
  }, [replies])

  const visible = useMemo(
    () =>
      replies
        .filter((r) => (filter === 'all' ? true : r.intent === filter))
        .filter((r) => (openOnly ? !r.doneAt : true)),
    [replies, filter, openOnly],
  )

  const toggleDone = async (reply: ReplyItem) => {
    const done = !reply.doneAt
    setReplies((rs) => rs.map((r) => (r.id === reply.id ? { ...r, doneAt: done ? Date.now() : null } : r)))
    patchReply(reply.id, { done, read: true }).catch(() => load())
  }

  const markRead = (reply: ReplyItem) => {
    if (reply.readAt) return
    setReplies((rs) => rs.map((r) => (r.id === reply.id ? { ...r, readAt: Date.now() } : r)))
    patchReply(reply.id, { read: true }).catch(() => {})
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Replies"
        subtitle="Every reply across your networks, sorted by what matters"
        icon={<MessageCircle size={20} />}
        actions={
          <button className="btn-ghost" onClick={load} title="Refresh">
            <RotateCcw size={15} />
            Refresh
          </button>
        }
      />

      <div className="flex items-center gap-2 border-b border-line px-6 py-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={clsx(
              'chip border transition',
              filter === f.key
                ? 'border-brand-200 bg-brand-50 text-brand-600'
                : 'border-line text-ink-600 hover:border-ink-300',
            )}
          >
            {f.label}
            <span className="ml-1.5 text-xs text-ink-400">{counts[f.key]}</span>
          </button>
        ))}
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs font-medium text-ink-500">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
            className="accent-brand-500"
          />
          Open only
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="grid h-full place-items-center">
            <Loader2 className="animate-spin text-brand-400" size={26} />
          </div>
        ) : error ? (
          <div className="grid h-full place-items-center p-8 text-center">
            <div>
              <p className="text-sm font-medium text-ink-700">Could not load replies</p>
              <p className="mt-1 text-xs text-ink-400">{error}</p>
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="grid h-full place-items-center p-8">
            <div className="max-w-sm text-center">
              <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-brand-50 text-brand-500">
                <MessageCircle size={28} />
              </div>
              <h2 className="font-display text-base font-bold text-ink-900">
                {replies.length === 0 ? 'No replies yet' : 'Nothing here'}
              </h2>
              <p className="mx-auto mt-1.5 text-sm text-ink-500">
                {replies.length === 0
                  ? 'When people reply to your published posts, they show up here, with leads and questions surfaced first.'
                  : 'Try another filter, or untick "Open only" to see handled replies.'}
              </p>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {visible.map((reply) => (
              <ReplyRow key={reply.id} reply={reply} onToggleDone={() => toggleDone(reply)} onSeen={() => markRead(reply)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function ReplyRow({
  reply,
  onToggleDone,
  onSeen,
}: {
  reply: ReplyItem
  onToggleDone: () => void
  onSeen: () => void
}) {
  const meta = INTENT_META[reply.intent]
  const unread = !reply.readAt && !reply.doneAt
  const author = reply.authorName || reply.authorHandle || 'Someone'

  return (
    <li
      className={clsx('flex items-start gap-3.5 px-6 py-4 transition', reply.doneAt && 'opacity-55')}
      onMouseEnter={onSeen}
    >
      {reply.authorAvatarUrl ? (
        <img src={reply.authorAvatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
      ) : (
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-panel font-display text-sm font-bold text-ink-500 ring-1 ring-line">
          {author.replace(/^@/, '').slice(0, 1).toUpperCase()}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={clsx('text-sm text-ink-900', unread ? 'font-bold' : 'font-medium')}>{author}</span>
          {reply.authorHandle && reply.authorName && (
            <span className="font-mono text-[11px] text-ink-400">{reply.authorHandle}</span>
          )}
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.05em]',
              meta.chip,
            )}
          >
            {meta.icon}
            {meta.label}
          </span>
          {reply.isHighSignal && !reply.doneAt && (
            <span className="inline-flex items-center rounded-full border border-accent-orange/40 bg-accent-orange/10 px-2 py-0.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.05em] text-accent-orange">
              High signal
            </span>
          )}
          <span className="ml-auto shrink-0 font-mono text-[10px] text-ink-400">
            {reply.repliedAt ? formatDistanceToNow(reply.repliedAt, { addSuffix: true }) : ''}
          </span>
        </div>

        <p className="mt-1 text-sm leading-relaxed text-ink-800">{reply.body || '(no text)'}</p>

        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-ink-400">
          {platformGlyph(reply.post.platform)}
          <span className="truncate">
            on {reply.post.channelName}: “{(reply.post.content || '').slice(0, 70)}
            {(reply.post.content || '').length > 70 ? '…' : ''}”
          </span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
        {reply.post.url && (
          <a
            href={reply.post.url}
            target="_blank"
            rel="noreferrer"
            className="grid h-8 w-8 place-items-center rounded-lg border border-line text-ink-400 transition-colors hover:text-ink-900"
            title="Open the live post to respond"
          >
            <ExternalLink size={14} />
          </a>
        )}
        <button
          onClick={onToggleDone}
          className={clsx(
            'grid h-8 w-8 place-items-center rounded-lg border transition-colors',
            reply.doneAt
              ? 'border-accent-green/40 bg-accent-green/10 text-accent-green'
              : 'border-line text-ink-400 hover:border-accent-green/60 hover:text-accent-green',
          )}
          title={reply.doneAt ? 'Reopen' : 'Mark handled'}
        >
          <Check size={14} />
        </button>
      </div>
    </li>
  )
}
